import { Box, IconButton, TextField } from '@mui/material';
import { FC, useEffect, useMemo } from 'react';
import Img from '../Img';
import styles from './MainTextSearchInput.styles';
import { actions, Store } from 'rdx';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import debounce from 'lodash.debounce';
import utils from 'utils';

type Props = {
  autoFocus?: boolean;
};

const normStr = utils.string.normalize;

const MainTextSearchInput: FC<Props> = ({ autoFocus }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { items, scope } = useSelector((store: Store) => store);

  const debouncedFiltering = useMemo(
    () => debounce((value: string, currentItems: Store['items'], currentScope: Store['scope']) => {
      dispatch(actions.items.setLoading(true));
      if (value === '') {
        dispatch(actions.items.changeFilteredList(currentItems.scopedBulls));
        setTimeout(() => dispatch(actions.items.setLoading(false)), 500);
        return;
      }
      const sourceIndexes = currentScope.segment === ''
        ? currentItems.bulls.map((_, index) => index)
        : currentItems.scopedBulls;
      const normalizedValue = normStr(value);
      const indexes = sourceIndexes.filter((index) => {
        const bull = currentItems.bulls[index];
        const searchableText = 'index' in bull
          ? `${bull.index?.searchableText ?? ''} ${bull.index?.keywords?.join(' ') ?? ''}`
          : '';

        return (
          normStr(searchableText).includes(normalizedValue) ||
          normStr(bull?.warName || bull.name).includes(normalizedValue) ||
          normStr(bull.registry ?? '').includes(normalizedValue) ||
          normStr(bull.CRVcode).includes(normalizedValue)
        );
      });
      dispatch(actions.items.changeFilteredList(indexes));
      setTimeout(() => dispatch(actions.items.setLoading(false)), 500);
    }, 500),
    [ dispatch ]
  );

  useEffect(() => {
    dispatch(actions.items.setLoading(true));
    debouncedFiltering(scope.filterValue, items, scope);
  }, [ items.bulls, items.scopedBulls, scope.filterValue, scope.segment, debouncedFiltering, dispatch ]);

  useEffect(() => () => {
    debouncedFiltering.cancel();
  }, [ debouncedFiltering ]);

  const handleChange = (value: string) => {
    if (value !== '' && !window.location.pathname.includes('/bull-search')) {
      navigate('bull-search');
    }
    dispatch(actions.scope.changeFilterValue(value));
    debouncedFiltering(value, items, { ...scope, filterValue: value });
  };

  const boldPlaceholder = (
    <span
      style={{
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        marginLeft: '60px',
        color: 'rgba(0, 0, 0, 0.54)',
        pointerEvents: 'none'
      }}
    >
      Digite o <b>nome</b>, <b>RGD</b> ou <b>Código CRV</b>
    </span>
  );

  return (
    <Box sx={styles.container}>
      <TextField
        fullWidth
        autoFocus={autoFocus}
        placeholder=" "
        value={scope.filterValue}
        onChange={(e) => handleChange(e.target.value)}
        InputProps={{
          startAdornment: (
            <IconButton>
              <Img url="/icons/search.svg" />
            </IconButton>
          ),
          ...(scope.filterValue !== ''
            ? {
              endAdornment: (
                <IconButton
                  onClick={() => {
                    dispatch(actions.scope.changeFilterValue(''));
                    debouncedFiltering('', items, { ...scope, filterValue: '' });
                  }}
                >
                  <Img url="/icons/close.svg" />
                </IconButton>
              ),
            }
            : {}),
        }}
      />
      {scope.filterValue !== '' ? null : boldPlaceholder}
    </Box>
  );
};

export default MainTextSearchInput;
